import {
  CButton,
  CCol,
  CForm,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CRow,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { getProgressNoteImage, getProgressNoteRegenerate } from 'src/helper/backendHelper'
import { putCollector, postCollector } from 'src/apiHelper/collectorApi'
import { COLLECTOR_URL } from '../../../apiHelper/apiRoute/collector'
import { isTestFeature } from '../../../utils/sharedFeaturesFunctions'
import FEATURES from '../../../utils/featuresConstants'
import { chartTxtTrim, downloadChartPDF } from '../../../utils/sharedChartFunctions'
import GenericConfirmModal from '../census/components/ConfirmationModals/GenericConfirmModal'
import { SYSTEM_ID } from '../sharedConstants'

const AddendumModal = ({
  visit,
  setIsAddendumOpen,
  onCreateAddendumSuccess = () => {},
  onCosign = () => {},
  hideButtons = false,
}) => {
  const copyText = chartTxtTrim(visit?.chart?.note_txt) || null
  const signedUserId = visit?.chart?.signed_user_id || null

  const [addendumTxt, setAddendumTxt] = useState('')
  const [noteImg, setNoteImg] = useState(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddendumBox, setShowAddendumBox] = useState(hideButtons)

  const provider = useSelector((state) => state?.user?.provider)
  const features = useSelector((state) => state?.user?.features)

  useEffect(() => {
    if (visit?._id) {
      getProgressNoteImage(visit._id, 'openAddModal').then((res) => {
        if (res?.status === 'success') {
          setNoteImg(res?.data)
        } else {
          setNoteImg(null)
        }
      })
    }
  }, [visit?._id])

  const onCreateAddendum = async () => {
    let url = COLLECTOR_URL.ADDENDDUM_TEXT
    await postCollector(url.replace('{visitId}', `${visit?._id}`), {
      addendum: addendumTxt.trim(),
    }).then(async (res) => {
      if (res?.status === 'success') {
        setIsAddendumOpen(false)
        setAddendumTxt('')
        toast.success('Addendum added successfully...')

        if (visit?._id) {
          getProgressNoteImage(visit._id, 'onCreateAdd').then((res) => {
            if (res?.status === 'success') {
              setNoteImg(res?.data)
            } else {
              setNoteImg(null)
            }
          })
        }
        onCreateAddendumSuccess()
      } else {
        toast.error('Oops... Failed to update Procedures. Please try again...')
      }
    })

    setTimeout(function () {
      setIsAddendumOpen(false)
    }, 1000)
  }

  const signerIsReal = signedUserId !== null && signedUserId !== SYSTEM_ID

  return (
    <>
      {showConfirmModal && (
        <GenericConfirmModal
          onConfirm={() => {
            setShowConfirmModal(false)
            putCollector(COLLECTOR_URL.COSIGN, { charge_id: visit?._id })
              .then((data) => {
                if (data.status === 'success') {
                  toast.success(data.message || 'Chart cosigned successfully')
                  setIsAddendumOpen(false)
                  onCosign()
                } else {
                  toast.error(data?.message || 'Failed to cosign chart')
                }
              })
              .catch((e) => {
                console.error(e)
                toast.error('Failed to cosign chart')
              })
          }}
          onClose={() => {
            setShowConfirmModal(false)
          }}
          content={`The note will be cosigned. This action can't be undone`}
        />
      )}
      <CModal
        className="addendum-modal"
        visible={true}
        onClose={() => setIsAddendumOpen(false)}
        size="lg"
        fullscreen="lg"
      >
        <CModalHeader className="d-flex">
          <div>
            <div className="title-holder">
              <h2 className="title">
                {visit?.patient_name} ({visit?.patient_dob})
              </h2>
              <p className="subtxt">
                ** Document e-signed by{' '}
                {visit?.rendering_provider_name ? visit?.rendering_provider_name : ''} , on{' '}
                {visit?.chart?.signed_on &&
                  format(new Date(visit?.chart?.signed_on), 'MMM dd, yyyy hh:mm a')}
              </p>
            </div>
            {!hideButtons && (
              <div>
                {signerIsReal && (
                  <CButton
                    color="secondary"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(copyText)
                      toast.success('Note Copied!')
                    }}
                  >
                    <i
                      className="fa-regular fa-copy"
                      style={{ marginRight: '4px', fontSize: '1.1em' }}
                    ></i>
                    Copy Text
                  </CButton>
                )}
                <CButton
                  color="secondary"
                  size="sm"
                  className="ms-2"
                  onClick={() => downloadChartPDF(visit)}
                  style={{ color: 'var(--black)' }}
                >
                  <i
                    style={{ fontSize: '1.2em', marginRight: '4px' }}
                    className="fa-light fa-cloud-arrow-down"
                  ></i>
                  Download
                </CButton>
                {features?.includes(FEATURES.VisitNotesPOC) &&
                  visit.rendering_provider_id !== provider?._id &&
                  !visit?.chart?.cosigned_on && (
                    <CButton
                      color="secondary"
                      size="sm"
                      className="ms-2"
                      onClick={() => {
                        setShowConfirmModal(true)
                      }}
                      style={{ width: '16%', color: 'var(--black)' }}
                    >
                      Co-Sign
                    </CButton>
                  )}

                {signerIsReal && (
                  <CButton
                    color="secondary"
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      setShowAddendumBox(true)
                    }}
                    disabled={showAddendumBox}
                    style={{ color: 'var(--black)' }}
                  >
                    Add Addendum
                  </CButton>
                )}

                {isTestFeature() && (
                  <CButton
                    color="danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => getProgressNoteRegenerate(visit?._id)}
                  >
                    <i className="fa-solid fa-rotate-right"></i> Regenerate Note image
                  </CButton>
                )}
              </div>
            )}
          </div>
        </CModalHeader>
        <CModalBody>
          <CRow
            className={
              signerIsReal && showAddendumBox && !hideButtons
                ? 'justify-content-between'
                : 'justify-content-center'
            }
          >
            {!hideButtons && (
              <CCol
                xs="6"
                md="6"
                xl="6"
                xxl="6"
                style={{ zIndex: '1', width: showAddendumBox ? 'revert-layer' : '80%' }}
              >
                {!signerIsReal && (
                  <div style={{ textAlign: 'center', marginTop: '-8px', paddingBottom: '8px' }}>
                    Note is ineligible for addendum
                  </div>
                )}
                <div className="model-body">
                  <div className="image-holder">
                    <img src={noteImg} alt="Note Img" style={{ width: '100%' }} />
                  </div>
                </div>
              </CCol>
            )}
            {showAddendumBox && (
              <CCol xs="6" md="6" xl="6" xxl="6">
                <div className="addendum-form-holder">
                  <div className="addendum-form">
                    <h1 className="title">New addendum</h1>
                    <CForm>
                      <CFormTextarea
                        id="addendum"
                        className={'default-text-area'}
                        rows="10"
                        text=""
                        onChange={(e) => setAddendumTxt(e.currentTarget.value)}
                      ></CFormTextarea>
                    </CForm>
                  </div>
                  <CModalFooter>
                    {!hideButtons && (
                      <CButton
                        color="secondary"
                        onClick={() => {
                          setShowAddendumBox(false)
                        }}
                      >
                        Cancel
                      </CButton>
                    )}

                    <CButton
                      color="primary"
                      disabled={addendumTxt.trim() === ''}
                      onClick={onCreateAddendum}
                    >
                      Create Addendum
                    </CButton>
                  </CModalFooter>
                </div>
              </CCol>
            )}
          </CRow>
        </CModalBody>
      </CModal>
    </>
  )
}

export default AddendumModal
